import { motion } from 'framer-motion'
import { User, MessageSquare, Copy, Globe, Zap } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { nip19 } from 'nostr-tools'
import { useNostrProfile } from '../hooks/useNostrProfile'
import { useNostrUserEvents } from '../hooks/useNostrUserEvents'
import { EntryCard } from '../components/Entry/EntryCard'
import { useMemo, useState } from 'react'
import { ProfileEditModal } from '../components/Profile/ProfileEditModal'
import { updateProfile } from '../lib/nostr'
import { useChatStore } from '../stores/chatStore'
import { ZapModal } from '../components/Social/ZapModal'
import { VerificationBadge } from '../components/Profile/VerificationBadge'
import { useFollowList } from '../hooks/useFollowList'
import { publishFollowList } from '../lib/nostr'
import { useToast } from '../contexts/ToastContext'
import { FollowersModal } from '../components/Profile/FollowersModal'
import { ProfileQRModal } from '../components/Profile/ProfileQRModal'
import { ProfileActions } from '../components/Profile/ProfileActions'
import { ProfileStats } from '../components/Profile/ProfileStats'
import { MediaGrid } from '../components/Profile/MediaGrid'
import { UserAvatar } from '../components/Common/UserAvatar'


export function ProfilePage() {
  const { username } = useParams<{ username: string }>()
  const { pubkey: currentUserPubkey, sk } = useAuth()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isZapModalOpen, setIsZapModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'entries' | 'media' | 'zaps' | 'about'>('entries')
  const { openChat } = useChatStore()
  const [isFollowLoading, setIsFollowLoading] = useState(false)
  const { showToast } = useToast()
  const [followersModalOpen, setFollowersModalOpen] = useState(false)
  const [followingModalOpen, setFollowingModalOpen] = useState(false)
  const [qrModalOpen, setQrModalOpen] = useState(false)

  // Determine the hex pubkey first
  const pubkey = useMemo(() => {
    const input = username || currentUserPubkey
    if (!input) return null

    if (input.startsWith('npub1')) {
      try {
        const decoded = nip19.decode(input) as any
        if (decoded.type === 'npub') {
          return decoded.data
        }
      } catch (e) {
        console.error('Failed to decode npub:', e)
        return null
      }
    }
    return input
  }, [username, currentUserPubkey])

  // Get current user's following list
  const { following, isFollowing, refresh: refreshFollowList, updateFollowing } = useFollowList(currentUserPubkey)

  // Handle copy to clipboard
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    showToast(`${label} kopyalandı!`, 'success')
  }

  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!currentUserPubkey || !pubkey || isFollowLoading) return

    setIsFollowLoading(true)

    try {
      const currentlyFollowing = isFollowing(pubkey)
      let newFollowingList: string[]

      if (currentlyFollowing) {
        newFollowingList = following.filter(p => p !== pubkey)
      } else {
        newFollowingList = [...following, pubkey]
      }

      // Immediately update UI (optimistic)
      updateFollowing(newFollowingList)

      // Publish to Nostr
      await publishFollowList(newFollowingList, sk)

      // Refresh from relays
      setTimeout(() => {
        refreshFollowList()
      }, 500)

      showToast(currentlyFollowing ? 'Takipten çıkıldı' : 'Takip edildi', 'success')
    } catch (error) {
      console.error('Follow toggle error:', error)
      showToast('Bir hata oluştu', 'error')
    } finally {
      setIsFollowLoading(false)
    }
  }

  const { metadata, isLoading } = useNostrProfile(pubkey)
  const { events, isLoading: eventsLoading, isLinking, loadMore } = useNostrUserEvents(pubkey)

  const isOwnProfile = pubkey === currentUserPubkey

  const profileName = metadata?.display_name || metadata?.name || (pubkey ? `${pubkey.slice(0, 8)}...` : 'Anonim')
  const npub = pubkey ? nip19.npubEncode(pubkey) : ''

  const handleSaveProfile = async (newMetadata: any) => {
    try {
      await updateProfile(newMetadata, sk)
    } catch (error) {
      console.error('Failed to update profile:', error)
      throw error
    }
  }

  const handleSendMessage = () => {
    if (pubkey) {
      openChat(pubkey)
    }
  }

  if (!pubkey) {
    return (
      <div className="text-center py-12">
        <User className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
        <h2 className="font-poppins font-semibold text-2xl mb-4">Profil Bulunamadı</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Görünüşe göre geçerli bir kullanıcı kimliği belirtilmemiş.
        </p>
      </div>
    )
  }

  const mappedEntries = events.map(event => {
    // Extract topic from tags if available
    const subjectTag = event.tags.find(t => t[0] === 'subject')?.[1]
    const topicTag = event.tags.find(t => t[0] === 't' && t[1] !== 'mavililer' && t[1] !== 'mavililersözlük')?.[1]
    const topicTitle = (subjectTag || topicTag || 'gündem').replace(/-/g, ' ')

    return {
      id: event.id,
      content: event.content,
      created_at: new Date(event.created_at * 1000).toISOString(),
      author_username: metadata?.display_name || metadata?.name || event.pubkey.slice(0, 8),
      author_pubkey: event.pubkey,
      author_avatar_url: metadata?.picture || null,
      author_reputation: 0,
      upvotes: 0,
      downvotes: 0,
      topicTitle: topicTitle,
    }
  })

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        {/* Profile Header - Hybrid Layout */}
        <div className="bg-white dark:bg-surface-900 rounded-2xl shadow-sm border border-surface-200 dark:border-surface-700 overflow-hidden">
          {/* Banner */}
          <div className="relative h-48 md:h-56 bg-gradient-to-br from-mavi-500 to-mavi-600" />

          {/* Profile Content */}
          <div className="relative px-4 md:px-6 pb-6">
            {/* Avatar and Action Buttons Row */}
            <div className="flex justify-between items-start mb-4">
              <div className="relative -mt-16 md:-mt-20">
                <UserAvatar
                  src={metadata?.picture}
                  name={profileName}
                  size="2xl"
                  isExternal={false}
                  className="shadow-xl"
                />
              </div>

              {/* Action Buttons */}
              <ProfileActions
                isOwnProfile={isOwnProfile}
                isFollowing={pubkey ? isFollowing(pubkey) : false}
                isFollowLoading={isFollowLoading}
                onEdit={() => setIsEditModalOpen(true)}
                onQR={() => setQrModalOpen(true)}
                onFollow={handleFollowToggle}
                onZap={() => setIsZapModalOpen(true)}
                onMessage={handleSendMessage}
              />
            </div>

            {/* Profile Info */}
            <div className="space-y-3">
              {/* Name and Badge */}
              <div className="flex items-center gap-3 flex-wrap">
                {!isLoading || metadata ? (
                  <div className="flex items-center gap-2">
                    <h1 className="font-poppins font-bold text-2xl md:text-3xl text-gray-900 dark:text-white">
                      {profileName}
                    </h1>
                    <VerificationBadge nip05={metadata?.nip05} />
                  </div>
                ) : (
                  <div className="h-8 w-48 bg-surface-200 dark:bg-surface-800 rounded animate-pulse" />
                )}
                {events.length > 0 && (
                  <span className={`px-2.5 py-1 rounded-lg text-[10px] font-poppins font-bold uppercase tracking-tight border ${events.length > 100
                    ? 'bg-mavi-500 text-white border-mavi-600'
                    : events.length > 10
                      ? 'bg-mavi-500/10 text-mavi-600 dark:text-mavi-400 border-mavi-200 dark:border-mavi-800'
                      : 'bg-surface-100 dark:bg-surface-800 text-gray-500 dark:text-gray-400 border-surface-200 dark:border-surface-700'
                    }`}>
                    {events.length > 100 ? 'Mavili' : events.length > 10 ? 'Yazar' : 'Çaylak'}
                  </span>
                )}
              </div>

              {/* Bio */}
              {metadata?.about && (
                <p className="text-sm text-gray-600 dark:text-gray-400 font-merriweather max-w-2xl">
                  {metadata.about}
                </p>
              )}

              {/* Stats Row */}
              <ProfileStats
                entryCount={events.length}
                karma={events.length * 10}
                followerCount={0}
                followingCount={following.length}
                onFollowersClick={() => setFollowersModalOpen(true)}
                onFollowingClick={() => setFollowingModalOpen(true)}
              />

              {/* Website Link */}
              {metadata?.website && (
                <a
                  href={metadata.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-mavi-600 dark:text-mavi-400 hover:underline font-poppins w-fit"
                >
                  <Globe className="w-4 h-4" />
                  {new URL(metadata.website).hostname}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-surface-900 rounded-2xl shadow-sm border border-surface-200 dark:border-surface-700 overflow-hidden">
          <div className="flex border-b border-surface-200 dark:border-surface-700">
            <button
              onClick={() => setActiveTab('entries')}
              className={`flex-1 px-6 py-4 font-poppins font-bold text-sm transition-colors relative ${activeTab === 'entries'
                ? 'text-mavi-600 dark:text-mavi-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Entry'ler
              {activeTab === 'entries' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-mavi-600 dark:bg-mavi-400"
                />
              )}
            </button>
            <button
              onClick={() => setActiveTab('media')}
              className={`flex-1 px-6 py-4 font-poppins font-bold text-sm transition-colors relative ${activeTab === 'media'
                ? 'text-mavi-600 dark:text-mavi-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Medya
              {activeTab === 'media' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-mavi-600 dark:bg-mavi-400"
                />
              )}
            </button>
            <button
              onClick={() => setActiveTab('zaps')}
              className={`flex-1 px-6 py-4 font-poppins font-bold text-sm transition-colors relative ${activeTab === 'zaps'
                ? 'text-mavi-600 dark:text-mavi-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Bahşişler
              {activeTab === 'zaps' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-mavi-600 dark:bg-mavi-400"
                />
              )}
            </button>
            <button
              onClick={() => setActiveTab('about')}
              className={`flex-1 px-6 py-4 font-poppins font-bold text-sm transition-colors relative ${activeTab === 'about'
                ? 'text-mavi-600 dark:text-mavi-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Hakkında
              {activeTab === 'about' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-mavi-600 dark:bg-mavi-400"
                />
              )}
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'entries' && (
              <div className="space-y-4">

                {
                  eventsLoading ? (
                    <div className="space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="animate-pulse bg-white dark:bg-surface-900 h-32 rounded-lg border border-surface-200 dark:border-surface-700"></div>
                      ))}
                    </div>
                  ) : mappedEntries.length > 0 ? (
                    <div className="space-y-4">
                      {mappedEntries.map((entry) => (
                        <EntryCard key={entry.id} entry={entry} fetchReactions={true} />
                      ))}

                      {mappedEntries.length > 0 && (
                        <div className="pt-8 flex justify-center">
                          <button
                            onClick={loadMore}
                            disabled={isLinking}
                            className="group relative flex items-center gap-2 px-8 py-3 bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 rounded-xl font-poppins font-bold text-sm text-gray-600 dark:text-gray-400 hover:text-mavi-600 dark:hover:text-mavi-400 hover:border-mavi-500/50 transition-all shadow-sm hover:shadow-md disabled:opacity-50"
                          >
                            {isLinking ? (
                              <div className="w-5 h-5 border-2 border-mavi-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <div className="flex items-center gap-2">
                                <span>Daha Fazlasını Yükle</span>
                                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-surface-100 dark:bg-surface-800 group-hover:bg-mavi-50 dark:group-hover:bg-mavi-950 transition-colors">
                                  <svg className="w-3 h-3 transition-transform group-hover:translate-y-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-20 bg-white dark:bg-surface-900 rounded-lg border border-dashed border-surface-300 dark:border-surface-700">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                      <p className="text-gray-500 dark:text-gray-400 font-poppins">
                        Henüz bu kullanıcının bir entry'si bulunamadı.
                      </p>
                    </div>
                  )
                }
              </div>
            )}

            {/* Media Tab */}
            {activeTab === 'media' && (
              <div className="space-y-4">
                {eventsLoading ? (
                  <div className="grid grid-cols-3 gap-1">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="animate-pulse bg-surface-100 dark:bg-surface-800 aspect-square"></div>
                    ))}
                  </div>
                ) : (
                  <MediaGrid events={events} />
                )}
              </div>
            )}


            {/* Zaps Tab */}
            {activeTab === 'zaps' && (
              <div className="text-center py-20 bg-white dark:bg-surface-900 rounded-lg border border-dashed border-surface-300 dark:border-surface-700">
                <Zap className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p className="text-gray-900 dark:text-white font-poppins font-bold mb-2">
                  Bahşiş Geçmişi
                </p>
                <p className="text-gray-500 dark:text-gray-400 font-poppins text-sm">
                  Gönderilen ve alınan bahşişler yakında burada görünecek.
                </p>
              </div>
            )}

            {/* About Tab */}
            {activeTab === 'about' && (
              <div className="space-y-8">
                <div className="space-y-3">
                  {metadata?.about ? (
                    <p className="text-gray-700 dark:text-gray-300 font-merriweather leading-relaxed whitespace-pre-wrap">
                      {metadata.about}
                    </p>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 font-poppins italic">
                      Henüz bir bio eklenmemiş.
                    </p>
                  )}
                </div>

                {/* Identity / Nostr Info */}
                <div className="space-y-6 pt-6 border-t border-surface-200 dark:border-surface-700">
                  <div className="space-y-4">
                    <h4 className="text-[10px] font-poppins font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">
                      Kimlik Bilgileri
                    </h4>

                    <div className="grid gap-4">
                      <div
                        onClick={() => handleCopy(npub, 'Kısa Kimlik')}
                        className="group p-4 bg-surface-50 dark:bg-surface-800/30 rounded-2xl border border-surface-100 dark:border-surface-800 hover:border-mavi-500/30 transition-all cursor-pointer"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[9px] font-poppins font-bold text-mavi-600 dark:text-mavi-400 uppercase tracking-widest">Kısa Kimlik (npub)</span>
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-mavi-500 transition-colors" />
                        </div>
                        <p className="text-[11px] md:text-xs font-mono text-gray-600 dark:text-gray-400 break-all leading-relaxed">
                          {npub}
                        </p>
                      </div>

                      <div
                        onClick={() => handleCopy(pubkey, 'Ham Kimlik')}
                        className="group p-4 bg-surface-50 dark:bg-surface-800/30 rounded-2xl border border-surface-100 dark:border-surface-800 hover:border-mavi-500/30 transition-all cursor-pointer"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[9px] font-poppins font-bold text-mavi-600 dark:text-mavi-400 uppercase tracking-widest">Ham Kimlik (hex)</span>
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-mavi-500 transition-colors" />
                        </div>
                        <p className="text-[11px] md:text-xs font-mono text-gray-600 dark:text-gray-400 break-all leading-relaxed">
                          {pubkey}
                        </p>
                      </div>
                    </div>
                  </div>

                  {metadata?.website && (
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-poppins font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">
                        Bağlantılar
                      </h4>
                      <a
                        href={metadata.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2.5 p-4 bg-surface-50 dark:bg-surface-800/30 rounded-2xl border border-surface-100 dark:border-surface-800 hover:text-mavi-600 dark:hover:text-mavi-400 transition-all group w-full"
                      >
                        <Globe className="w-4 h-4 text-gray-400 group-hover:text-mavi-500" />
                        <span className="text-sm font-poppins font-medium">{metadata.website}</span>
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div >

      <ProfileEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        initialData={metadata}
        onSave={handleSaveProfile}
      />
      <ZapModal
        isOpen={isZapModalOpen}
        onClose={() => setIsZapModalOpen(false)}
        recipientName={profileName}
        lnAddress={metadata?.lud16}
      />

      {/* Modals */}
      <FollowersModal
        isOpen={followersModalOpen}
        onClose={() => setFollowersModalOpen(false)}
        pubkeys={[]} // Will be implemented with reverse lookup
        title="Takipçiler"
      />
      <FollowersModal
        isOpen={followingModalOpen}
        onClose={() => setFollowingModalOpen(false)}
        pubkeys={following}
        title="Takip Edilenler"
      />
      <ProfileQRModal
        isOpen={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        pubkey={pubkey}
        profileName={profileName}
      />
    </>
  )
}


